import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, ConfirmDialog, NewSoonBadge, useToast } from "@a2z/ui";
import { joinLocalized } from "@/lib/formatting/list";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { usePaymentOptionsQuery } from "@/modules/payment-options/services/usePaymentOptionsQuery";
import { useUpsertPaymentOptionMutation } from "@/modules/payment-options/services/useUpsertPaymentOptionMutation";
import { useDeletePaymentOptionMutation } from "@/modules/payment-options/services/useDeletePaymentOptionMutation";
import { useUpdateStoreSettingsMutation } from "@/modules/settings/services/useUpdateStoreSettingsMutation";
import {
  WALLET_PROVIDERS,
  type WalletProviderValue,
} from "@/modules/onboarding/constants/wallet-providers";
import {
  entriesToGuided,
  guidedToEntries,
} from "@/modules/payment-options/lib/payment-option-mapping";
import { Card } from "./primitives";
import { MethodCard } from "./payment/MethodCard";
import { WalletFields } from "./payment/WalletFields";
import { InstaPayFields } from "./payment/InstaPayFields";
import { DepositCard } from "./payment/DepositCard";
import { PaymentGatewaysSection } from "@/modules/settings/components/integrations/PaymentGatewaysSection";
import {
  CardLogo,
  CodLogo,
  InstaPayLogo,
  WalletCluster,
} from "./payment/payment-logos";
import {
  hasConfiguredOnline,
  hasOnlineEnabled,
  hydrate,
  isInstaConfigured,
  isWalletConfigured,
  PHONE_RE,
  type InstaState,
  type PanelForm,
} from "./payment/payment-form";

type PaymentPanelProps = {
  storeSlug: string;
  locale: string;
  depositSettings: { type: "percent" | "flat"; amount: number } | null;
  includeShippingInOrderDeposit: boolean;
  acceptsCod: boolean;
  autoConfirmCodOrders: boolean;
  isMobile?: boolean;
};

export function PaymentPanel({
  storeSlug,
  depositSettings,
  includeShippingInOrderDeposit,
  acceptsCod,
  autoConfirmCodOrders,
}: PaymentPanelProps) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();

  const { data: options, isLoading } = usePaymentOptionsQuery({ storeSlug });
  const { mutateAsync: upsertOption } = useUpsertPaymentOptionMutation();
  const { mutateAsync: deleteOption } = useDeletePaymentOptionMutation();
  const { mutateAsync: updateSettings } = useUpdateStoreSettingsMutation();

  const [form, setForm] = useState<PanelForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmKind, setConfirmKind] = useState<"echo" | "pause" | null>(null);
  const hydratedRef = useRef(false);
  // Hydration snapshot for dirty-checking. State (not a ref) because `dirty`
  // is derived from it during render — react-hooks/refs forbids render reads.
  const [initialSnapshot, setInitialSnapshot] = useState<string>("");
  const onlineGroupRef = useRef<HTMLDivElement>(null);

  // Hydrate the editable form once the existing options load. BANK options are
  // preserved (not surfaced); a legacy BANK_ACCOUNT InstaPay receiver triggers
  // a one-time re-confirm nudge instead of being silently changed.
  useEffect(() => {
    if (hydratedRef.current || !options) return;
    hydratedRef.current = true;
    const next = hydrate(options, acceptsCod, autoConfirmCodOrders);
    setForm(next);
    setInitialSnapshot(JSON.stringify(next));
  }, [options, acceptsCod, autoConfirmCodOrders]);

  // Existing online options for reconcile on save. Bank is intentionally never
  // tracked here, so save can never delete a BANK option.
  const existing = useMemo(() => {
    const walletByProvider = new Map<WalletProviderValue, string>();
    let instaId: string | undefined;
    for (const opt of options ?? []) {
      const g = entriesToGuided(opt.type, opt.entries);
      if (g.kind === "wallet" && g.provider)
        walletByProvider.set(g.provider, opt.id);
      else if (g.kind === "instapay" && !instaId) instaId = opt.id;
    }
    return { walletByProvider, instaId };
  }, [options]);

  const dirty = form !== null && JSON.stringify(form) !== initialSnapshot;
  const onlineConfigured = form ? hasConfiguredOnline(form) : false;
  const codOffNoOnline = Boolean(form && !form.cod && !hasOnlineEnabled(form));

  // Warn on unsaved method changes when leaving the tab/refreshing.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const patch = (next: Partial<PanelForm>): void =>
    setForm((prev) => (prev ? { ...prev, ...next } : prev));

  const toggleWalletProvider = (provider: WalletProviderValue): void => {
    if (!form) return;
    const has = form.wallets.some((w) => w.provider === provider);
    patch({
      wallets: has
        ? form.wallets.filter((w) => w.provider !== provider)
        : [...form.wallets, { provider, phone: "", prefilled: false }],
    });
  };

  const setWalletPhone = (provider: WalletProviderValue, phone: string): void => {
    if (!form) return;
    patch({
      wallets: form.wallets.map((w) =>
        w.provider === provider ? { ...w, phone, prefilled: false } : w,
      ),
    });
  };

  const validate = (f: PanelForm): string | null => {
    if (f.walletOn) {
      if (f.wallets.length === 0)
        return t(keys.onboarding.paymentWalletSelectOne);
      for (const w of f.wallets) {
        if (!PHONE_RE.test(w.phone.trim()))
          return t(keys.onboarding.paymentWalletInvalidPhone);
      }
    }
    if (f.instaOn) {
      if (f.insta.receiver === "PHONE" && !PHONE_RE.test(f.insta.phone.trim()))
        return t(keys.onboarding.paymentInstaPayInvalidPhone);
      if (f.insta.receiver === "IPA" && f.insta.ipa.trim().length < 3)
        return t(keys.onboarding.paymentInstaPayEnterIPA);
      if (f.insta.receiver === "BANK_ACCOUNT") {
        if (!f.insta.bankName) return t(keys.onboarding.paymentBankSelectError);
        if (!f.insta.holder.trim()) return t(keys.onboarding.paymentBankHolderError);
        if (f.insta.account.trim().length < 5)
          return t(keys.onboarding.paymentBankAccountError);
      }
      if (f.insta.receiver === "CARD") {
        if (f.insta.cardNumber.trim().length < 6) return t(keys.errors.required);
        if (!f.insta.cardHolder.trim()) return t(keys.errors.required);
      }
    }
    return null;
  };

  // Persist methods + COD. Deposit is owned by DepositSettingsForm; we never
  // clear it here (the silent-wipe-on-online-off bug is fixed by omission).
  const persist = async (f: PanelForm): Promise<void> => {
    setSaving(true);
    try {
      if (f.cod !== acceptsCod) {
        await updateSettings({ storeSlug, acceptsCod: f.cod });
      }
      if (f.autoConfirmCod !== autoConfirmCodOrders) {
        await updateSettings({
          storeSlug,
          autoConfirmCodOrders: f.autoConfirmCod,
        });
      }

      // Wallets — reconcile against existing WALLET options by provider.
      const desired = new Set(f.walletOn ? f.wallets.map((w) => w.provider) : []);
      for (const [provider, id] of existing.walletByProvider) {
        if (!desired.has(provider)) await deleteOption({ storeSlug, detailId: id });
      }
      if (f.walletOn) {
        for (const w of f.wallets) {
          await upsertOption({
            storeSlug,
            detailId: existing.walletByProvider.get(w.provider),
            type: "WALLET",
            isActive: true,
            entries: guidedToEntries({
              kind: "wallet",
              provider: w.provider,
              phone: w.phone,
            }),
          });
        }
      }

      // InstaPay — single option.
      if (f.instaOn) {
        await upsertOption({
          storeSlug,
          detailId: existing.instaId,
          type: "INSTAPAY",
          isActive: true,
          entries: guidedToEntries({
            kind: "instapay",
            receiver: f.insta.receiver,
            phone: f.insta.phone,
            ipa: f.insta.ipa,
            cardNumber: f.insta.cardNumber,
            cardHolder: f.insta.cardHolder,
            bankName: f.insta.bankName,
            holder: f.insta.holder,
            account: f.insta.account,
          }),
        });
      } else if (existing.instaId) {
        await deleteOption({ storeSlug, detailId: existing.instaId });
      }

      hydratedRef.current = false; // re-hydrate from refreshed options
      toast.success(t(keys.seller.paymentOptionsSavedNotice));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t(keys.errors.generic));
    } finally {
      setSaving(false);
    }
  };

  // Save entry point: validate → (deposit-pause warning) or (echo-back confirm)
  // or persist directly.
  const handleSave = (): void => {
    if (!form) return;
    const methodError = validate(form);
    if (methodError) {
      setError(methodError);
      return;
    }
    setError(null);

    const baseline = JSON.parse(initialSnapshot) as PanelForm;
    const turningOffLastOnline =
      hasOnlineEnabled(baseline) &&
      !hasOnlineEnabled(form) &&
      (depositSettings !== null || includeShippingInOrderDeposit);
    if (turningOffLastOnline) {
      setConfirmKind("pause");
      return;
    }
    if (hasOnlineEnabled(form)) {
      setConfirmKind("echo");
      return;
    }
    void persist(form);
  };

  const confirmedSave = (): void => {
    setConfirmKind(null);
    if (form) void persist(form);
  };

  // Buyer-facing receiver summary shown in the echo-back confirm dialog.
  const receiverSummary = (f: PanelForm): string => {
    const parts: string[] = [];
    if (f.walletOn) {
      for (const w of f.wallets) {
        const p = WALLET_PROVIDERS.find((x) => x.value === w.provider);
        const label = locale === "ar" ? p?.labelAr : p?.labelEn;
        if (w.phone.trim()) parts.push(`${label}: ⁦${w.phone.trim()}⁩`);
      }
    }
    if (f.instaOn && isInstaConfigured(f.insta)) {
      const v =
        f.insta.receiver === "PHONE"
          ? f.insta.phone
          : f.insta.receiver === "IPA"
            ? f.insta.ipa
            : f.insta.receiver === "BANK_ACCOUNT"
              ? f.insta.account
              : f.insta.cardNumber;
      parts.push(`${t(keys.paymentOptions.typeInstapay)}: ⁦${v.trim()}⁩`);
    }
    return parts.join("  ·  ");
  };

  // Status summary line (what buyers currently accept).
  const statusLine = (f: PanelForm): string => {
    const labels: string[] = [];
    if (f.cod) labels.push(t(keys.seller.codTitle));
    if (f.walletOn && isWalletConfigured(f.wallets)) {
      for (const w of f.wallets) {
        const p = WALLET_PROVIDERS.find((x) => x.value === w.provider);
        labels.push((locale === "ar" ? p?.labelAr : p?.labelEn) ?? w.provider);
      }
    }
    if (f.instaOn && isInstaConfigured(f.insta))
      labels.push(t(keys.paymentOptions.typeInstapay));
    return labels.length > 0
      ? t(keys.seller.pmStatusAccepting, { methods: joinLocalized(labels, locale) })
      : t(keys.seller.pmStatusEmpty);
  };

  const scrollToOnline = (): void =>
    onlineGroupRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div id="payment-settings" className="flex flex-col gap-4">
      <Card
        title={t(keys.seller.pmSectionTitle)}
        desc={t(keys.seller.pmSectionSubtitle)}
      >
        {error ? (
          <div className="mb-3">
            <Alert message={error} helperText={t(keys.errors.recovery)} />
          </div>
        ) : null}

        {form ? (
          <p
            className={[
              "mb-3 text-xs font-medium",
              statusLine(form) === t(keys.seller.pmStatusEmpty)
                ? "text-[color:var(--error-500)]"
                : "text-[color:var(--text-secondary)]",
            ].join(" ")}
          >
            {statusLine(form)}
          </p>
        ) : null}

        {isLoading || !form ? (
          <div className="flex flex-col gap-2.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[58px] rounded-xl bg-[color:var(--surface-alt)]"
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Receive payment */}
            <div className="flex flex-col gap-2">
              <GroupLabel>{t(keys.seller.pmGroupReceive)}</GroupLabel>
              <MethodCard
                logo={<CodLogo />}
                title={t(keys.seller.codTitle)}
                helper={t(keys.seller.codHelper)}
                checked={form.cod}
                onToggle={(cod) => patch({ cod })}
              />
              {form.cod ? (
                <MethodCard
                  logo={<CodLogo />}
                  title={t(keys.seller.storeSettings.autoConfirmCodTitle)}
                  helper={t(keys.seller.storeSettings.autoConfirmCodHelper)}
                  checked={form.autoConfirmCod}
                  onToggle={(autoConfirmCod) => patch({ autoConfirmCod })}
                />
              ) : null}
              {codOffNoOnline ? (
                <Alert message={t(keys.seller.pmCodOffWarning)} />
              ) : null}
            </div>

            {/* Online payment */}
            <div ref={onlineGroupRef} className="flex flex-col gap-2">
              <GroupLabel hint={t(keys.seller.pmGroupOnlineHint)}>
                {t(keys.seller.pmGroupOnline)}
              </GroupLabel>
              <p className="text-xs leading-relaxed text-[color:var(--text-muted)]">
                {t(keys.seller.pmOnlineProofLine)}
              </p>

              <MethodCard
                logo={<WalletCluster active={form.walletOn} />}
                title={t(keys.paymentOptions.typeWallet)}
                helper={t(keys.seller.pmWalletsHelper)}
                checked={form.walletOn}
                onToggle={(walletOn) => patch({ walletOn })}
              >
                <WalletFields
                  wallets={form.wallets}
                  onToggleProvider={toggleWalletProvider}
                  onPhoneChange={setWalletPhone}
                />
              </MethodCard>

              <MethodCard
                logo={<InstaPayLogo active={form.instaOn} />}
                title={t(keys.paymentOptions.typeInstapay)}
                checked={form.instaOn}
                onToggle={(instaOn) => patch({ instaOn })}
              >
                <InstaPayFields
                  value={form.insta}
                  onChange={(insta: InstaState) => patch({ insta })}
                />
              </MethodCard>

              <MethodCard
                logo={<CardLogo />}
                title={t(keys.seller.pmCardComingTitle)}
                helper={t(keys.seller.pmCardComingHelper)}
                disabled
                trailing={<NewSoonBadge label={t(keys.seller.pmComingSoon)} />}
              />
            </div>

            <div className="flex justify-end">
              <Button
                variant="primary"
                size="md"
                disabled={saving || !dirty}
                onClick={handleSave}
              >
                {saving ? t(keys.common.loading) : t(keys.common.save)}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Deposit — locked until an online method is configured. */}
      {form ? (
        <DepositCard
          storeSlug={storeSlug}
          depositSettings={depositSettings}
          includeShippingInOrderDeposit={includeShippingInOrderDeposit}
          unlocked={onlineConfigured}
          onRequestEnable={scrollToOnline}
        />
      ) : null}

      {/* Online payment gateways — buyers pay by card/wallet via the seller's
          own connected gateway account. */}
      <PaymentGatewaysSection storeSlug={storeSlug} />

      {/* Echo-back confirm — the receiver-correctness gate. */}
      <ConfirmDialog
        isOpen={confirmKind === "echo"}
        intent="confirm"
        title={t(keys.seller.pmConfirmTitle)}
        body={`${t(keys.seller.pmConfirmBody)}${form ? `\n\n${receiverSummary(form)}` : ""}`}
        confirmLabel={t(keys.seller.pmConfirmButton)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={saving}
        onConfirm={confirmedSave}
        onCancel={() => setConfirmKind(null)}
      />

      {/* Deposit-pause warning — never silently wipe the deposit. */}
      <ConfirmDialog
        isOpen={confirmKind === "pause"}
        intent="danger"
        title={t(keys.seller.pmDepositPauseTitle)}
        body={t(keys.seller.pmDepositPauseBody)}
        confirmLabel={t(keys.seller.pmDepositPauseConfirm)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={saving}
        onConfirm={confirmedSave}
        onCancel={() => setConfirmKind(null)}
      />
    </div>
  );
}

function GroupLabel({
  children,
  hint,
}: {
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs font-bold uppercase tracking-wide text-[color:var(--text-muted)]">
        {children}
      </span>
      {hint ? (
        <span className="text-xs text-[color:var(--text-muted)]">{hint}</span>
      ) : null}
    </div>
  );
}
